import React, { memo, useContext, useRef, useState } from "react";
import { Col, Row, Skeleton, Typography, DatePicker, Button, message, Tag, Input, Table, Tooltip } from "antd";
import { EyeOutlined, SearchOutlined, PlusOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

import dayjs from "dayjs";
import PropTypes from "prop-types";
import { formatShowStandarDate, reduceText } from "../../../../utils/Formaters";
import { bank_reconciliation_manager } from "../../../../services";
import { formatCurrency, includePercentageAtInvoicesData, TextForInvoicePosition } from "../../utils";
import { currencyGetter } from "../../../../utils/BankCurrencyUtils";
import { ModalInvoiceDetails } from "./ModalDetails";
import { CompaniesContext } from "../../../../contexts";

const filterInvoicesByPercentage = (value, record) => {
	const { percentage } = record;
	if (value === "no") return percentage === 0;
	if (value === "intermediate") return percentage > 0 && percentage < 100;
	if (value === "completed") return percentage === 100;
	return false;
};

export const MiniTableInvoices = memo(({
	token, 
	invoices = [],
	invoicesToSend = {},
	companyId,
	bankAccountId,
	enableSelectInvoices = false,
	getAllInvoices = false,
	searchText = "",
	lastInvoicesId = {},
	onSendInvoice = () => {},
	onInvoices = () => {},
	onChangeSearch = () => {},
	onSendLastInvoices = () => {},
}) => {
	const modalInvoiceDetailsRef = useRef(null);
	
	const {in: lastInvoiceIn = null, out: lastInvoiceOut = null} = lastInvoicesId;
	const { selectedCompany: company } = useContext(CompaniesContext);
	
	const [dates, setDates] = useState({
		firstMonth: "",
		lastMonth: "",
	});
	const [loading, setLoading] = useState(false);	

	// Filter Options
	const fetchInvoices = async() => {
		if(dates.firstMonth !== "" && dates.lastMonth !== ""){
			const checkingStartDate = dayjs(new Date(dates.firstMonth));
			const checkingEndDate = dayjs(new Date(dates.lastMonth));

			const monthDifference = checkingEndDate.diff(checkingStartDate, "month", true);
			if (monthDifference > 5){
				message.error("El rango de fechas no puede superar los 5 meses.");
				return;
			}
		}

		setLoading(true);

		try {			
			const data = {
				bank_account_id: bankAccountId,
				company_id: companyId,
				start_date: dates.firstMonth,
				end_date: dates.lastMonth,
				get_all_invoices: getAllInvoices,
			};
			
			const res = await bank_reconciliation_manager(token, "invoice", data);

			if(!res){
				setLoading(false);
				return;
			}

			const { invoices: invoicesToSet, lastInvoiceOutId = null, lastInvoiceInId = null } = res;			
			onInvoices(includePercentageAtInvoicesData(invoicesToSet));
			onSendLastInvoices({ in: lastInvoiceInId, out: lastInvoiceOutId });
		} catch (err) {
			message.error(err.response.data);
		} finally {
			setLoading(false);
		}		
	};

	const loadMoreInvoices = async() => {
		if(dates.firstMonth !== "" && dates.lastMonth !== ""){
			const checkingStartDate = dayjs(new Date(dates.firstMonth));
			const checkingEndDate = dayjs(new Date(dates.lastMonth));

			const monthDifference = checkingEndDate.diff(checkingStartDate, "month", true);
			if (monthDifference > 5){
				message.error("El rango de fechas no puede superar los 5 meses.");
				return;
			}
		}

		setLoading(true);

		try {			
			const data = {
				bank_account_id: bankAccountId,
				company_id: companyId,
				start_date: dates.firstMonth === "" ? null : dates.firstMonth,
				end_date: dates.lastMonth === "" ? null : dates.lastMonth,
				last_element_for_out: lastInvoiceOut,
				last_element_for_in: lastInvoiceIn,
				get_all_invoices: getAllInvoices,
			};
			
			const res = await bank_reconciliation_manager(token, "invoice", data);

			if(!res){
				setLoading(false);
				return;
			}

			const { invoices: invoicesToSet, lastInvoiceOutId = null, lastInvoiceInId = null } = res;			
			onInvoices([...invoices, ...includePercentageAtInvoicesData(invoicesToSet)]);
			message.info(invoicesToSet.length === 0 ? "No se encontraron más facturas" : `Se han cargado ${invoicesToSet.length} factura(s)`);
			if(invoicesToSet.length > 0){
				const invoiceToSetIn = lastInvoiceInId ?? lastInvoiceIn;
				const invoiceToSetOut = lastInvoiceOutId ?? lastInvoiceOut;
				onSendLastInvoices({ in: invoiceToSetIn, out: invoiceToSetOut });
			}
		} catch (err) {
			message.error(err.response.data);
		} finally {
			setLoading(false);
		}		
	};

	const dataSource = invoices.map((item) => {
		const {
			id,
			date,
			number = "N/A",
			reference,
			currency,
			total,
			position,
			percentage = 0.0,
			reconciliation: {
				in: transactionsCoveringIn = {},
				out: transactionsCoveringOut = {},
				invoice_in_paid: inInvoicePaid = false,
				invoice_out_paid: outInvoicePaid = false,
			} = {},
			sender_name = null,
			receptor_name = null,
		} = item;

		const selectedTransactionsCovering = position === "in" ? transactionsCoveringIn : transactionsCoveringOut;
		const totalCoveredSelectedConvering =
			Object.values(selectedTransactionsCovering).reduce((acc, item) => acc + item.amount, 0) ?? 0;

		const finalEntity = receptor_name ?? sender_name ?? "N/A";
		const entityType = sender_name ? "Emisor" : "Receptor";

		return {
			key: id,
			id,
			date,
			number,
			reference,
			currency,
			total,
			position,
			percentage,
			finalEntity,
			entityType,
			totalCoveredSelectedConvering,
			inInvoicePaid,
			outInvoicePaid,
		};
	});
	
	const columns = [
		{
			title: "Fecha",
			dataIndex: "date",
			key: "date",
			width: 100,
			align: "center",
			render: (text) => (
				<span style={{ whiteSpace: "nowrap" }}>
					<Text>{formatShowStandarDate(text)}</Text>
				</span>
			),
		},
		{
			title: "Entidad",
			dataIndex: "finalEntity",
			key: "finalEntity",
			align: "center",
			width: 100,
			render: (text, record) => (
				<Tooltip placement="topLeft" title={`${record.entityType}: ${text}`}>{reduceText(text, 5)}</Tooltip>
			),
		},
		{
			title: "N. Serie",
			dataIndex: "reference",
			key: "reference",
			align: "center",
			width: 100,
		},
		{
			title: "Número",
			dataIndex: "number",
			key: "number",
			align: "center",
			width: 100,
			render: (text) => text ?? "N/A",
		},
		{
			title: "Total",
			dataIndex: "total",
			key: "total",
			width: 120,
			align: "right",
			render: (text, record) => (
				<span style={{ whiteSpace: "nowrap" }}>
					{currencyGetter[record.currency]} {formatCurrency(text)}
				</span>
			),
		},
		{
			title: "Pagado",
			dataIndex: "totalCoveredSelectedConvering",
			key: "totalCoveredSelectedConvering",
			width: 120,
			align: "right",
			filters: [
				{ text: "Sin Pagar", value: "no" },
				{ text: "Pendiente", value: "intermediate" },
				{ text: "Completado", value: "completed" },
			],			
			onFilter: filterInvoicesByPercentage,
			render: (text, record) => (
				<Tooltip placement="topRight" title={`Consumido: ${record.percentage} %`}> 
					{currencyGetter[record.currency]} {formatCurrency(text)} 
				</Tooltip>
			),
		},
		{
			title: "Tipo",
			dataIndex: "position",
			key: "position",
			width: 50,
			align: "center",				
			filters: [
				{ text: "Compra", value: "out" },
				{ text: "Venta", value: "in" },
			],
			onFilter: (value, record) => record.position.indexOf(value) === 0,
			render: (text) => {
				const { color = "black", label = "N/A" } = TextForInvoicePosition[text] || {};
				return <Tag color={color}>{label}</Tag>;
			},
		},
		{
			title: "Acciones",
			dataIndex: "actions",
			key: "actions",
			width: 30,
			align: "center",
			render: (_, record) => (
				<Tooltip placement="topRight" title="Ver Más">
					<Button variant="outlined" onClick={() => {
						const invoicesFounded = invoices.filter((invoice) => invoice.id === record.id);

						if(invoicesFounded.length === 0){
							return;
						}
						
						// setSelectedInvoiceToView(invoicesFounded[0]);
						modalInvoiceDetailsRef.current?.openModal(invoicesFounded[0]);
					}}>
						<EyeOutlined />
					</Button>
				</Tooltip>
			),
		},
	];
	
	const TableInvoicesView = (<>
		<Title level={3} style={{textAlign: "center", marginBottom: "7px", marginTop: "12px"}}>Facturas</Title>

		<Row justify="center" align="middle" style={{ flexWrap: "wrap" }}>
			<Col style={{ marginLeft: "20px", marginRight: "10px", marginBottom: "15px" }}>
				<RangePicker 
					style={{ width: "300px" }} 
					picker="month"
					value={[
						dates.firstMonth !== "" ? dayjs(dates.firstMonth) : null,
						dates.lastMonth !== "" ? dayjs(dates.lastMonth) : null,
					]}
					onChange={(_, dateString) => {
						setDates({ firstMonth: dateString[0], lastMonth: dateString[1] });
					}}
				/>
			</Col>

			<Col style={{ marginBottom: "15px", marginRight: "10px" }}>
				<Button type="primary" onClick={fetchInvoices}> <SearchOutlined /> </Button>
			</Col>

			<Col style={{ marginBottom: "15px", marginRight: "10px" }}>
				<Button icon={<PlusOutlined />} type="primary" onClick={loadMoreInvoices}> Cargar Más </Button>
			</Col>

			<Col style={{ marginBottom: "15px", marginRight: "10px" }} flex="auto">
				<Input
					placeholder="Buscar Factura"
					value={searchText}
					style={{ marginRight: "10px", width: "100%" }}
					onChange={(e) => onChangeSearch(e.target.value)}
					prefix={<SearchOutlined />}
				/>
			</Col>
		</Row>
		
		<Table
			size="small"
			columns={columns}
			dataSource={dataSource}
			pagination={false}
			style={{ marginRight: "15px", marginLeft: "15px", overflowY: "auto" }}
			rowClassName={(record) => (invoicesToSend[record.id] ? "selected" : "")}
			rowSelection={{
				type: "checkbox",
				selectedRowKeys: Object.keys(invoicesToSend),
				onChange: (selectedRowKeys, selectedRows) => {
					if (!enableSelectInvoices) {
						message.warning("Por favor selecciona una transacción");
						return;
					}

					// Copia el estado actual de invoicesToSend
					let updatedInvoices = { ...invoicesToSend };

					// Agregar los seleccionados
					selectedRows.forEach(({ id }) => {
						updatedInvoices[id] = {};
					});

					// Filtrar solo los IDs seleccionados y eliminar los que ya no están seleccionados
					updatedInvoices = Object.fromEntries(
						Object.entries(updatedInvoices).filter(([key]) => selectedRowKeys.includes(key))
					);

					// Obtener las listas de cambios
					const listPreviousInvoices = Object.keys(invoicesToSend);
					const listNewInvoices = Object.keys(updatedInvoices);

					const addedInvoices = listNewInvoices.filter(id => !listPreviousInvoices.includes(id));
					const removedInvoices = listPreviousInvoices.filter(id => !listNewInvoices.includes(id));

					// Si se agregó un solo invoice
					if (addedInvoices.length === 1) {
						const idInvoice = addedInvoices[0];
						const invoice = invoices.find((invoice) => invoice.id === idInvoice) || {};
						const { reconciliation: { invoice_in_paid = false, invoice_out_paid = false } = {} } = invoice;

						onSendInvoice({
							invoiceId: idInvoice,
							checked: true,
							inInvoicePaid: invoice_in_paid,
							outInvoicePaid: invoice_out_paid,
						});
						return;
					}

					// Si se eliminó un solo invoice
					if (removedInvoices.length === 1) {
						const idInvoice = removedInvoices[0];

						onSendInvoice({
							invoiceId: idInvoice,
							checked: false,
							inInvoicePaid: false,
							outInvoicePaid: false,
						});
					}
				},
			}}
		/>

		<ModalInvoiceDetails
			ref={modalInvoiceDetailsRef}
			company={company}
		/>
	</>);

	return (
		<>
			{!loading && TableInvoicesView}
			{loading && <Skeleton active/>}
		</>
	);
});

MiniTableInvoices.displayName = "MiniTableInvoices";

MiniTableInvoices.propTypes = {
	token: PropTypes.string.isRequired,
	invoices: PropTypes.array,
	invoicesToSend: PropTypes.object.isRequired,
	containerRef: PropTypes.object,
	companyId: PropTypes.string.isRequired,
	bankAccountId: PropTypes.string.isRequired,
	enableSelectInvoices: PropTypes.bool,
	getAllInvoices: PropTypes.bool,
	searchText: PropTypes.string,
	filterOptions: PropTypes.object,
	lastInvoicesId: PropTypes.shape({
		in: PropTypes.string,
		out: PropTypes.string,
	}),
	onSendInvoice: PropTypes.func,
	onInvoices: PropTypes.func,
	onChangeSearch: PropTypes.func,
	onSendLastInvoices: PropTypes.func,
};